import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { useSettings, settingsImageUrl } from "../context/SettingsContext";
import { COLOR_PRESETS, TEXT_GROUPS } from "../lib/constants";
import { toast } from "sonner";
import { Save, Upload, Leaf, Check, Loader2 } from "lucide-react";

export default function Personalizzazione() {
  const { settings, reload } = useSettings();
  const [form, setForm] = useState({ app_name: "", slogan: "", primary_color: "160 84% 39%", logo_file_id: null, login_bg_file_id: null, texts: {} });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");

  useEffect(() => {
    if (settings) setForm({
      app_name: settings.app_name || "",
      slogan: settings.slogan || "",
      primary_color: settings.primary_color || "160 84% 39%",
      logo_file_id: settings.logo_file_id || null,
      login_bg_file_id: settings.login_bg_file_id || null,
      texts: settings.texts || {},
    });
  }, [settings]);

  const setText = (key, val) => setForm((f) => ({ ...f, texts: { ...f.texts, [key]: val } }));

  const upload = async (field, file) => {
    if (!file) return;
    setUploading(field);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, [field]: data.file_id }));
      toast.success("Immagine caricata — ricordati di salvare");
    } catch (err) { toast.error(apiError(err)); }
    setUploading("");
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", form);
      await reload();
      toast.success("Personalizzazione salvata");
    } catch (err) { toast.error(apiError(err)); }
    setSaving(false);
  };

  const input = "w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 outline-none focus:border-primary";
  const logoPreview = form.logo_file_id ? settingsImageUrl("logo_file_id", { logo_file_id: form.logo_file_id, updated_at: form.logo_file_id }) : null;
  const bgPreview = form.login_bg_file_id ? settingsImageUrl("login_bg_file_id", { login_bg_file_id: form.login_bg_file_id, updated_at: form.login_bg_file_id }) : null;

  return (
    <div className="animate-fade-up max-w-3xl">
      <h1 className="font-display font-black text-4xl text-slate-50 tracking-tight mb-1">Personalizza</h1>
      <p className="text-slate-400 mb-6">Rendi l'app tua: nome, logo, colore e immagine di login.</p>

      <div className="bg-slate-900/50 border border-slate-800 rounded-md p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-400 mb-1.5">Nome app</label>
            <input data-testid="setting-app-name" value={form.app_name} onChange={(e) => setForm({ ...form, app_name: e.target.value })} className={input} placeholder="OrtoFrutta" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-400 mb-1.5">Sottotitolo</label>
            <input data-testid="setting-slogan" value={form.slogan} onChange={(e) => setForm({ ...form, slogan: e.target.value })} className={input} placeholder="Manager" />
          </div>
        </div>

        <div>
          <label className="block text-xs uppercase tracking-wider text-slate-400 mb-2">Colore principale</label>
          <div className="flex flex-wrap gap-2" data-testid="color-presets">
            {COLOR_PRESETS.map((c) => (
              <button
                key={c.value}
                data-testid={`color-${c.name}`}
                onClick={() => setForm({ ...form, primary_color: c.value })}
                className={`w-10 h-10 rounded-md border-2 flex items-center justify-center transition-transform hover:scale-105 ${form.primary_color === c.value ? "border-white" : "border-transparent"}`}
                style={{ backgroundColor: `hsl(${c.value})` }}
                title={c.name}
              >
                {form.primary_color === c.value && <Check className="w-4 h-4 text-white" />}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-400 mb-2">Logo</label>
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-md bg-slate-800 border border-slate-700 flex items-center justify-center overflow-hidden">
                {logoPreview ? <img src={logoPreview} alt="logo" className="w-full h-full object-cover" /> : <Leaf className="w-6 h-6 text-primary" />}
              </div>
              <label data-testid="logo-upload-label" className="flex items-center gap-2 px-3 py-2 rounded-md bg-slate-800 border border-slate-700 text-sm text-slate-200 cursor-pointer hover:bg-slate-700 transition-colors">
                {uploading === "logo_file_id" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Carica logo
                <input data-testid="logo-upload-input" type="file" accept="image/*" className="hidden" onChange={(e) => upload("logo_file_id", e.target.files?.[0])} />
              </label>
            </div>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-400 mb-2">Sfondo login</label>
            <div className="flex items-center gap-3">
              <div className="w-24 h-14 rounded-md bg-slate-800 border border-slate-700 flex items-center justify-center overflow-hidden">
                {bgPreview ? <img src={bgPreview} alt="bg" className="w-full h-full object-cover" /> : <span className="text-xs text-slate-500">nessuna</span>}
              </div>
              <label data-testid="bg-upload-label" className="flex items-center gap-2 px-3 py-2 rounded-md bg-slate-800 border border-slate-700 text-sm text-slate-200 cursor-pointer hover:bg-slate-700 transition-colors">
                {uploading === "login_bg_file_id" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Carica sfondo
                <input data-testid="bg-upload-input" type="file" accept="image/*" className="hidden" onChange={(e) => upload("login_bg_file_id", e.target.files?.[0])} />
              </label>
            </div>
          </div>
        </div>

        <button data-testid="settings-save-btn" onClick={save} disabled={saving} className="bg-primary text-slate-950 font-semibold rounded-md px-5 py-2.5 flex items-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salva personalizzazione
        </button>
      </div>

      <div className="bg-slate-900/50 border border-slate-800 rounded-md p-6 mt-6" data-testid="texts-section">
        <h2 className="font-display font-bold text-xl text-slate-50 mb-1">Testi dell'app</h2>
        <p className="text-slate-400 text-sm mb-5">Modifica i testi mostrati nella home e nelle pagine. Lascia vuoto per usare il testo predefinito.</p>
        <div className="space-y-6">
          {TEXT_GROUPS.map((g) => (
            <div key={g.group} data-testid={`text-group-${g.group}`}>
              <div className="text-xs uppercase tracking-[0.2em] text-primary mb-2">{g.group}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {g.items.map((it) => (
                  <div key={it.key} className={it.multiline ? "md:col-span-2" : ""}>
                    <label className="block text-xs uppercase tracking-wider text-slate-400 mb-1.5">{it.label}</label>
                    {it.multiline ? (
                      <textarea
                        data-testid={`text-${it.key}`}
                        rows={2}
                        value={form.texts?.[it.key] ?? ""}
                        onChange={(e) => setText(it.key, e.target.value)}
                        className={input + " resize-none"}
                        placeholder={it.def}
                      />
                    ) : (
                      <input
                        data-testid={`text-${it.key}`}
                        value={form.texts?.[it.key] ?? ""}
                        onChange={(e) => setText(it.key, e.target.value)}
                        className={input}
                        placeholder={it.def}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <button data-testid="settings-save-texts-btn" onClick={save} disabled={saving} className="mt-6 bg-primary text-slate-950 font-semibold rounded-md px-5 py-2.5 flex items-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salva testi
        </button>
      </div>
    </div>
  );
}
